import React from 'react';
import { useRouter } from 'next/navigation';
import Autocomplete from '@/components/form/inputs/Autocomplete';
import setAlertValue from '@/components/alert';
import SubmitButton from '@/components/SubmitButton';
import { Option } from '@/context/profile-builder';
import {
  sendEvent,
  trackClickEvent,
  useIntersectionObserver,
} from '@/utils/site-track';
import { RESUME_EVALUATION_LOADING_ROUTER_PATH } from '@/constants/router-path';
import { searchByQuery, uploadEvaluationResume } from '@/api/tanzhi';

const ExpectJobTitle = (props: {
  uploading?: boolean;
  dataEleGroup?: string;
  setUploading?: React.Dispatch<React.SetStateAction<boolean>>;
  onSuccess?: (data: { id: string; task_id: string }) => void;
  resumeFile?: File | null;
  autoCompleteClassName?: string;
  submitButtonClassName?: string;
  placeHolder?: string;
  needResumeFile?: boolean;
  value?: Option | null;
  handleUpload?: (
    expectJobTitle: Option | null,
    resumeFile?: File | null,
  ) => Promise<any>;
  onNextStep?: (expectJobTitle: Option) => void;
  smallSize?: boolean;
  title?: React.ReactNode;
}) => {
  const {
    resumeFile,
    onSuccess,
    uploading,
    setUploading,
    placeHolder = '请输入目标岗位',
    autoCompleteClassName = 'w-full mb-12 tablet:w-[90%]',
    submitButtonClassName = 'w-full !h-13.5 !ml-auto tablet:!w-[130px]',
    dataEleGroup,
    needResumeFile = true,
    value = null,
    handleUpload = uploadEvaluationResume,
    onNextStep,
    smallSize,
    title = (
      <p
        className={`mb-4 ${smallSize ? 'text-sm' : 'text-sm font-semibold tablet:text-base'}`}
      >
        请问您的目标岗位是？<span className="text-red-600">*</span>
      </p>
    ),
  } = props;

  const [expectJobTitle, setExpectJobTitle] = React.useState<Option | null>(
    value,
  );
  const [observerEls, setObserverEls] = React.useState<Element[]>([]);
  const expectJobTitleRef = React.useRef(null);
  const router = useRouter();

  const handleNextStep = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (needResumeFile && !resumeFile) {
      setAlertValue({ msg: '请先上传简历', severity: 'warning' });
      return;
    }
    if (!expectJobTitle || !expectJobTitle.name) {
      setAlertValue({ msg: '请填写目标岗位', severity: 'warning' });
      return;
    }
    if (dataEleGroup) {
      trackClickEvent(e);
    }
    onNextStep?.(expectJobTitle);
    setUploading?.(true);
  };

  const handleUploadResume = () => {
    if (
      !expectJobTitle ||
      !expectJobTitle.name ||
      (!resumeFile && needResumeFile)
    ) {
      return;
    }
    handleUpload(expectJobTitle, resumeFile)
      .then((res) => {
        const data = res.data;
        if (onSuccess) {
          onSuccess(data);
        } else {
          const { id } = data;
          router.push(`${RESUME_EVALUATION_LOADING_ROUTER_PATH}?id=${id}`);
        }
      })
      .catch((err) =>
        setAlertValue({
          msg: err.response.data?.detail || '诊断简历失败，请重试！',
        }),
      )
      .finally(() => {
        setUploading?.(false);
      });
  };

  React.useEffect(() => {
    if (!expectJobTitle && value) {
      setExpectJobTitle(value);
    }
  }, [value]);

  useIntersectionObserver(observerEls);

  React.useEffect(() => {
    if (expectJobTitleRef.current && !observerEls.length && dataEleGroup) {
      setObserverEls([expectJobTitleRef.current]);
    }
  }, [expectJobTitleRef.current]);

  return (
    <div
      data-ele-group={dataEleGroup}
      data-ele-id="expect_job_title"
      ref={expectJobTitleRef}
    >
      {title}
      <Autocomplete
        value={expectJobTitle}
        className={autoCompleteClassName}
        openAtFirstClick={false}
        getOptions={(query) => searchByQuery(query, 'name_search', false)}
        noOptionsText="抱歉，未找到您输入的职位名称，请重试。"
        placeholder={placeHolder}
        getOptionLabel={(item: Option | string) =>
          typeof item === 'string' ? item : item.name
        }
        onChange={(value) => {
          setExpectJobTitle({
            id: value?.id || '',
            name: value?.name || '',
          });
          sendEvent({
            name: 'click',
            ele_id: 'ac_填写目标职位',
            ele_group: dataEleGroup,
          });
        }}
        isOptionEqualToValue={(option, value) => option?.id === value?.id}
      />
      <div className="flex mt-auto">
        <SubmitButton
          submit={!!uploading}
          submitText="下一步"
          className={submitButtonClassName}
          handleSubmit={handleUploadResume}
          handleClick={handleNextStep}
          dataEleId="btn_目标职位下一步"
          dataEleGroup={dataEleGroup}
        />
      </div>
    </div>
  );
};

export default ExpectJobTitle;
